import classnames from 'classnames';
import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import AnimatedNumber from 'react-awesome-animated-number';

import Col from '../../../common/Col';
import Row from '../../../common/Row';
import {normalizeMoney} from '../../mechanics';
import GameAudio, {GameAudioHandle} from '../GameAudio';
import {SimplePill} from '../SimplePill';

const SCROLL_ANIMATION_MS = 1000;
const COLOR_ANIMATION_MS = 500;

function convertRemToPixels(rem: number) {
  return Math.floor(rem * parseFloat(getComputedStyle(document.documentElement).fontSize));
}

type Props = {
  id?: string;
  icon: ReactNode;
  label?: string;
  number: number;
  numberPrefix?: string;
  numberSuffix?: string;
  hasComma: boolean;
  danger?: boolean;
  normalizeValue?: boolean;
  playSound?: boolean; // defaults to true
  compact?: boolean;
};

const AnimatedNumberPill = ({
  id,
  label,
  number,
  numberPrefix,
  numberSuffix,
  hasComma,
  danger,
  normalizeValue = false,
  icon,
  playSound = true,
  compact = false,
}: Props) => {
  const audioRef = useRef<GameAudioHandle>(null);

  const [value, setValue] = useState(number);
  const [animationColor, setAnimationColor] = useState<string | null>(null);

  useEffect(() => {
    let timerToStartColorAnimation: NodeJS.Timeout;
    let timerToEndColorAnimation: NodeJS.Timeout;

    setValue(prevValue => {
      if (prevValue === number) return number;
      const color = prevValue < number ? 'text-primary-500' : 'text-red-200';
      setTimeout(() => {
        setAnimationColor(color);
        audioRef.current?.play();
      }, SCROLL_ANIMATION_MS);
      setTimeout(() => setAnimationColor(null), SCROLL_ANIMATION_MS + COLOR_ANIMATION_MS);
      return number;
    });

    return () => {
      clearTimeout(timerToStartColorAnimation);
      clearTimeout(timerToEndColorAnimation);
    };
  }, [number]);

  const pillContent = useMemo(
    () => (
      <Row className="space-x-5 justify-between items-center text-center leading-none">
        <Col>
          <div className="flex ml-2 my-0.5 h-[4.5rem] w-[4.5rem] align-center items-center">{icon}</div>
        </Col>
        <Col className={'grow font-medium gap-1'}>
          {label && <Row className="text-xs">{label}</Row>}
          <span className={'sr-only'}>
            {`${normalizeValue ? Math.floor(value) : value} ${numberPrefix ?? ''} ${numberSuffix ?? ''}`}
          </span>
          <Row
            className={classnames('text-xl mobile:font-semibold', animationColor, danger && 'text-red-200')}
            aria-hidden
          >
            {numberPrefix}
            <AnimatedNumber
              value={normalizeValue ? normalizeMoney(value) : value}
              hasComma={hasComma}
              size={convertRemToPixels(2.6)}
              duration={SCROLL_ANIMATION_MS}
            />
            {numberSuffix}
          </Row>
        </Col>
      </Row>
    ),
    [animationColor, danger, hasComma, icon, label, normalizeValue, numberPrefix, numberSuffix, value]
  );

  if (compact) return pillContent;

  return (
    <SimplePill id={id} className={'!w-auto !px-xs ml-[0.4rem] !mr-0'}>
      {playSound && <GameAudio ref={audioRef} src="sounds/ping.mp3" />}
      {pillContent}
    </SimplePill>
  );
};

export default AnimatedNumberPill;
